/**
 * TeamSystem Impact - Compliance Path · Phase Model
 *
 * Funzioni pure che calcolano stato e percentuale di avanzamento di ogni tappa
 * del "respiro" della compliance a partire dai dati reali delle API.
 * Niente React qui: logica isolata e testabile.
 */

export type PhaseState = 'not_started' | 'in_progress' | 'completed';

export interface PhaseResult {
  state: PhaseState;
  /** 0-100, intero */
  percent: number;
}

const clampPct = (n: number): number => {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
};

// ---------------------------------------------------------------------------
// Stakeholder Engagement
// ---------------------------------------------------------------------------

export interface EngagementInput {
  campaignsTotal: number;
  campaignsClosed: number;
  responses: number;
}

export function engagementPhase(i: EngagementInput): PhaseResult {
  if (i.campaignsTotal <= 0) return { state: 'not_started', percent: 0 };
  const percent = clampPct((i.campaignsClosed / i.campaignsTotal) * 100);
  if (i.campaignsClosed >= i.campaignsTotal) return { state: 'completed', percent: 100 };
  return { state: 'in_progress', percent };
}

// ---------------------------------------------------------------------------
// Doppia Materialità (DMA)
// ---------------------------------------------------------------------------

export interface DmaInput {
  /** IRO totali in catalogo per l'anno */
  total: number;
  /** IRO valutati (material + not_material) */
  assessed: number;
}

export function dmaPhase(i: DmaInput): PhaseResult {
  if (i.total <= 0 || i.assessed <= 0) return { state: 'not_started', percent: 0 };
  const percent = clampPct((i.assessed / i.total) * 100);
  if (percent >= 100) return { state: 'completed', percent: 100 };
  return { state: 'in_progress', percent };
}

/** Campi dell'IRO che servono a capire se la decisione appartiene all'anno */
export interface DmaIroLike {
  status?: string;
  materialityDecision?: string | null;
  createdAt?: string;
  validatedAt?: string;
  approvedAt?: string;
  impactMaterialityAssessment?: { assessedAt?: string } | null;
  financialMaterialityAssessment?: { assessedAt?: string } | null;
}

// Stesso criterio di getStats lato backend: decisione registrata su un IRO valutato
const DECIDED_STATUSES = ['validated', 'material', 'not_material'];

function hasDecision(iro: DmaIroLike): boolean {
  return DECIDED_STATUSES.includes(iro.status ?? '')
    && (iro.materialityDecision === 'material' || iro.materialityDecision === 'not_material');
}

/**
 * Margine sotto cui una valutazione anteriore alla creazione NON è un riporto.
 * L'import Excel fissa assessedAt un attimo prima di creare l'IRO (pochi ms);
 * il clone con "copia valutazioni" porta date di settimane o mesi prima.
 */
const CARRY_OVER_TOLERANCE_MS = 60 * 60 * 1000;

/**
 * Riporto dall'anno precedente: la valutazione più recente dell'IRO è anteriore
 * alla sua creazione, oltre la tolleranza (clonazione con "copia valutazioni").
 * validatedAt/approvedAt entrano nel confronto perché il clone li azzera: se ci
 * sono, sono stati registrati nell'anno. Senza createdAt o senza alcuna data di
 * valutazione non si può dire che sia un riporto (dati storici): non lo è.
 */
export function isCarriedOverAssessment(iro: DmaIroLike): boolean {
  const created = Date.parse(iro.createdAt ?? '');
  if (!Number.isFinite(created)) return false;
  const times = [
    iro.impactMaterialityAssessment?.assessedAt,
    iro.financialMaterialityAssessment?.assessedAt,
    iro.validatedAt,
    iro.approvedAt,
  ]
    .map((d) => Date.parse(d ?? ''))
    .filter(Number.isFinite);
  if (times.length === 0) return false;
  return Math.max(...times) < created - CARRY_OVER_TOLERANCE_MS;
}

/** IRO valutati nell'anno (con decisione e non riportati) e, tra questi, i materiali */
export function countDmaAssessedInYear(iros: DmaIroLike[]): { assessed: number; material: number } {
  let assessed = 0;
  let material = 0;
  for (const iro of iros) {
    if (!hasDecision(iro) || isCarriedOverAssessment(iro)) continue;
    assessed++;
    if (iro.materialityDecision === 'material') material++;
  }
  return { assessed, material };
}

/**
 * Come countDmaAssessedInYear, ma se l'elenco IRO non è disponibile (chiamata
 * fallita) ripiega sulle statistiche aggregate, che non distinguono i riporti.
 */
export function dmaAssessedWithFallback(
  iros: DmaIroLike[] | null,
  byStatus?: { material?: number; not_material?: number },
): { assessed: number; material: number } {
  if (iros) return countDmaAssessedInYear(iros);
  const material = byStatus?.material ?? 0;
  return { assessed: material + (byStatus?.not_material ?? 0), material };
}

/**
 * La matrice di materialità va mostrata inline solo se la DMA è terminata
 * e i punteggi sono consolidati: una matrice "a metà" sarebbe fuorviante.
 */
export function isDmaConsolidated(i: DmaInput): boolean {
  return dmaPhase(i).state === 'completed';
}

// ---------------------------------------------------------------------------
// Raccolta Dati
// ---------------------------------------------------------------------------

export interface CollectionInput {
  configurationComplete: boolean;
  collectionComplete: boolean;
  datapointCount: number;
  /** completionRate dell'API progress, 0-100 */
  completionRate: number;
}

export function collectionPhase(i: CollectionInput): PhaseResult {
  if (!i.configurationComplete && i.datapointCount <= 0) {
    return { state: 'not_started', percent: 0 };
  }
  if (i.collectionComplete) return { state: 'completed', percent: 100 };
  return { state: 'in_progress', percent: clampPct(i.completionRate) };
}
